import { FUNCTIONS_BASE } from "../utils/functionsBase";
import { createContext, useContext, useState, useEffect } from "react";
import { db, auth, appCheck } from "../firebase/config";
import {
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  confirmPasswordReset,
} from "firebase/auth";
import {
  getDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { getToken } from "firebase/app-check";
import { CONSENT_VERSION } from "../utils/consent";
import { sessionAadharFields, withoutPlainAadhar } from "../utils/idMask";

// FIX (Finding 5, App Check): loginLookup/recordFailedLogin are plain
// HTTP functions, not Firestore/Auth SDK calls — App Check only
// auto-attaches its token to actual SDK requests, so a fetch() call
// like these needs the token fetched and attached by hand. Returns ""
// (rather than throwing) if it fails, so a transient App Check hiccup
// degrades to "server says unauthorized" instead of breaking login
// with an unrelated-looking JS error.
export async function getAppCheckHeader() {
  try {
    const { token } = await getToken(appCheck, /* forceRefresh */ false);
    return { "X-Firebase-AppCheck": token };
  } catch (err) {
    console.error("getAppCheckHeader: failed to get App Check token:", err);
    return {};
  }
}

// REMOVED: the old "anonymous auth" trick (ensureAnonAuth + authLinks) is
// no longer needed. Every account now has a REAL Firebase Auth login
// (created by the Setup Wizard / bulk upload). Once someone signs in,
// request.auth.uid IS their real account id — no hidden link table
// required for the database's security rules to know who they are.

// Apps Script Web App /exec URLs — replace with the real deployment URLs.
// LOGIN_LOOKUP_URL: mobile -> authEmail (+ active/lock pre-checks).
// CHANGED (2026-08-12): moved off the Apps Script version (intermittent
// redirect-layer failures on Google's side) to a Firebase Cloud Function —
// same logic, more reliable.
// FORGOT_PASSWORD_URL: mobile -> triggers Firebase's own reset email
// server-side; never returns the real email to the browser.
const LOGIN_LOOKUP_URL = `${FUNCTIONS_BASE}/loginLookup`;
const RECORD_FAILED_LOGIN_URL = `${FUNCTIONS_BASE}/recordFailedLogin`;
// ADDED (DPDP 2, 29 Sept 2026): saves the person's "I agree" to the privacy
// notice. Done by a server function so nobody can fake or edit their own
// consent record from the browser.
const RECORD_CONSENT_URL = `${FUNCTIONS_BASE}/recordConsent`;
const FORGOT_PASSWORD_URL = "https://script.google.com/macros/s/AKfycbwx27IWmIKVcFFWu5r98rZDDEG1E7pOmJIurb_LGXD-Ev6FjN_AMEJwg_Cm7FNWz15J/exec";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);         // full user object (account + current-society details merged)
  const [loading, setLoading] = useState(true);   // checking localStorage on mount

  // ADDED (DPDP switch): is the DPDP feature set (consent screen, My Privacy,
  // Privacy Requests) switched on? null = still checking. Controlled from the
  // CRM (crm.adyantam.in) -- nobody edits the database by hand. If nothing
  // has ever been saved, or the check fails, it stays ON (the safer side).
  const [dpdpEnabled, setDpdpEnabled] = useState(null);

  useEffect(() => {
    if (!user?.uid) { setDpdpEnabled(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        if (typeof auth.authStateReady === "function") await auth.authStateReady();
        const snap = await getDoc(doc(db, "settings", "dpdp"));
        if (!cancelled) setDpdpEnabled(snap.exists() ? snap.data().enabled !== false : true);
      } catch (err) {
        console.error("DPDP switch check failed, keeping DPDP on:", err);
        if (!cancelled) setDpdpEnabled(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Restore session from localStorage on app load
  useEffect(() => {
    try {
      const saved = localStorage.getItem("abc_user");
      if (saved) setUser(JSON.parse(saved));
    } catch {
      localStorage.removeItem("abc_user");
    }
    setLoading(false);
    // REMOVED: no more "establish anonymous identity on page load" step —
    // there is nothing to establish anymore; real sign-in happens at login().
  }, []);

  // ── Helper: look up a society's friendly display name ──────────
  // Used to build the "Which society?" picker list with names like
  // "BSKR" instead of the internal code like "ABC-K7M2X9".
  async function getSocietyDisplayName(societyId) {
    try {
      const snap = await getDoc(doc(db, "societies", societyId, "settings", "init"));
      if (snap.exists()) {
        const d = snap.data();
        return d.name || d.societyName || societyId;
      }
    } catch {
      // fall through to fallback below
    }
    return societyId; // fallback — better to show the code than nothing
  }

  // ── Helper: fetch a person's per-society details and build the ──
  // ── full session object by merging account info + society info ──
  async function fetchMemberAndBuildSession(baseUserData, uid, societyId) {
    // ── Access expiry check ─────────────────────────────────
    // status "trial"   → block once trialEndsAt has passed (old behavior).
    // status "active"  → paid; block once subscriptionEndDate has passed
    //                     with no renewal recorded (status flips to
    //                     "expired" separately — this is the fallback if
    //                     that flip hasn't happened yet).
    // status "expired" → always block, regardless of any date.
    // No status (older/draft societies) → fall back to the old
    //                     trialEndsAt-only check, so nothing already
    //                     live regresses.
    let trialWarningDaysLeft = null;
    try {
      const societySnap = await getDoc(doc(db, "societies", societyId));
      const societyData = societySnap.exists() ? societySnap.data() : {};
      const { status, trialEndsAt, subscriptionEndDate } = societyData;

      if (status === "expired") {
        return {
          success: false,
          error: "Your subscription has ended. Please contact support@adyantam.in to renew.",
        };
      }

      const accessEndsAt =
        status === "active" ? subscriptionEndDate :
        status === "trial"  ? trialEndsAt :
        trialEndsAt; // no/unknown status — old fallback

      const expiredMessage =
        status === "active"
          ? "Your subscription has ended. Please contact support@adyantam.in to renew."
          : "Your trial period has ended. Please contact support@adyantam.in";

      if (accessEndsAt) {
        const endDate = new Date(accessEndsAt);
        const now = new Date();
        if (now >= endDate) {
          return { success: false, error: expiredMessage };
        }
        const msLeft = endDate.getTime() - now.getTime();
        const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
        if (daysLeft <= 7) {
          trialWarningDaysLeft = daysLeft;
        }
      }
    } catch (err) {
      console.error("fetchMemberAndBuildSession: access expiry check failed:", err);
      return { success: false, error: "Something went wrong. Please check your connection and try again." };
    }

    let memberSnap;
    try {
      memberSnap = await getDoc(doc(db, "societies", societyId, "members", uid));
    } catch (err) {
      console.error("fetchMemberAndBuildSession: member lookup failed:", err);
      return { success: false, error: "Something went wrong. Please check your connection and try again." };
    }

    if (!memberSnap.exists()) {
      return {
        success: false,
        error: "No membership record found for this society. Please contact your MC.",
      };
    }

    const m = memberSnap.data();
    const roles = Array.isArray(m.roles) ? m.roles : (m.role ? [m.role] : []);

    const sessionUser = {
      uid,
      mobile: baseUserData.mobile,
      name: baseUserData.name,
      currentSocietyId: societyId,
      societies: baseUserData.societies || [societyId],
      roles,
      flatIds: m.flatIds || (m.flatId ? [m.flatId] : []),
      memberType: m.memberType || "owner",
      paysMaintenance: m.paysMaintenance ?? false,
      paysWater: m.paysWater ?? false,
      emailId: m.emailId || baseUserData.emailId || null,
      // Finding 13: the full Aadhar number is never loaded into the
      // browser session — only the locked reference and last 4 digits.
      ...sessionAadharFields(m),
      entryDate: m.entryDate || null,
      exitDate: m.exitDate || null,
      familyMembers: (m.familyMembers || []).map(withoutPlainAadhar),
      vehicleNos: m.vehicleNos || [],
      upiIds: m.upiIds || [],
      mustChangePassword: baseUserData.mustChangePassword || false,
      // ADDED (DPDP 2): which version of the privacy notice this person
      // agreed to (null = never agreed). App.jsx shows the consent screen
      // whenever this differs from the current notice version.
      consentVersion: baseUserData.consentVersion || null,
      consentAt: baseUserData.consentAt || null,
      trialWarningDaysLeft,
    };

    return { success: true, user: sessionUser };
  }

  // ── LOGIN ──────────────────────────────────────────────────
  // CHANGED (2026-08-12): the "users" collection is no longer publicly
  // readable (was a full PII leak — anyone could read every resident's
  // name/mobile/society list with no login). Mobile -> authEmail lookup,
  // plus the active/lockout pre-checks that used to read straight off
  // that public doc, now happen server-side in the Login Lookup Apps
  // Script instead (see LoginLookup_AppsScript.gs).
  async function login(mobile, password) {
    const trimMobile = mobile.trim();

    let lookup;
    try {
      const resp = await fetch(LOGIN_LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await getAppCheckHeader()) },
        body: JSON.stringify({ mobile: trimMobile }),
      });
      lookup = await resp.json();
    } catch (err) {
      console.error("login: lookup script failed:", err);
      return { success: false, error: "Something went wrong. Please check your connection and try again." };
    }

    if (!lookup.success) {
      // Covers: not registered, deactivated, locked, not fully set up —
      // same messages the old pre-checks used to return.
      return { success: false, error: lookup.error || "Something went wrong. Please try again." };
    }

    try {
      let passwordMatch = false;
      try {
        await signInWithEmailAndPassword(auth, lookup.authEmail, password);
        passwordMatch = true;
      } catch {
        // Firebase returns various error codes (wrong password, no such
        // account, too many attempts, etc.) — we treat them all the same
        // way here and just show "incorrect password", same as before.
        passwordMatch = false;
      }

      if (!passwordMatch) {
        // FIX (Finding 8): the old client-side write to Firestore here
        // always silently failed, since the browser isn't signed in as
        // this uid on a wrong password — security rules correctly
        // blocked it. Now handled by a trusted backend function instead.
        try {
          await fetch(RECORD_FAILED_LOGIN_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(await getAppCheckHeader()) },
            body: JSON.stringify({ mobile: trimMobile }),
          });
        } catch (err) {
          console.error("login: failed to record failed attempt:", err);
        }
        return { success: false, error: "Incorrect password." };
      }

      // Signed in — request.auth.uid now matches this account's doc, so
      // reading it is allowed under the new rules.
      const uid = auth.currentUser.uid;
      const userSnap = await getDoc(doc(db, "users", uid));
      if (!userSnap.exists()) {
        return { success: false, error: "Something went wrong. Please try again." };
      }
      const data = userSnap.data();

      // ── Email-change sync ─────────────────────────────────────────
      // If this account confirmed a verifyBeforeUpdateEmail() change
      // (My Profile → change email) since their last login, Firebase
      // Auth's real email now differs from Firestore's authEmail/emailId.
      // Auth is the source of truth once confirmed — bring Firestore in
      // line with it now, for every society this account belongs to.
      // NOTE: users-doc sync and member-doc sync are checked and retried
      // INDEPENDENTLY every login — if the member-doc write ever fails
      // (e.g. a permissions issue), it must not get silently skipped
      // forever just because the users doc already matches.
      const confirmedEmail = auth.currentUser.email;

      if (confirmedEmail && confirmedEmail !== data.authEmail) {
        await updateDoc(doc(db, "users", uid), {
          authEmail: confirmedEmail,
          emailId: confirmedEmail,
        });
        data.authEmail = confirmedEmail;
        data.emailId = confirmedEmail;
      }

      if (confirmedEmail) {
        const societyIds = Array.isArray(data.societies) ? data.societies : [];
        for (const sid of societyIds) {
          try {
            const memberRef = doc(db, "societies", sid, "members", uid);
            const memberSnap = await getDoc(memberRef);
            if (memberSnap.exists() && memberSnap.data().emailId !== confirmedEmail) {
              await updateDoc(memberRef, { emailId: confirmedEmail });
            }
          } catch (err) {
            console.error(`login: failed to sync emailId for society ${sid}:`, err);
          }
        }
      }

      // ── Success — reset failed attempts (this write now works, since
      // we're authenticated as this same uid) ──────────────────────
      await updateDoc(doc(db, "users", uid), {
        failedAttempts: 0,
        lockedUntil: null,
      });

      const societyIds = Array.isArray(data.societies) ? data.societies : [];

      if (societyIds.length === 0) {
        return {
          success: false,
          error: "No society is linked to this account. Please contact your MC.",
        };
      }

      if (societyIds.length === 1) {
        const result = await fetchMemberAndBuildSession(data, uid, societyIds[0]);
        if (!result.success) return result;

        setUser(result.user);
        localStorage.setItem("abc_user", JSON.stringify(result.user));
        return { success: true, user: result.user };
      }

      // Two or more societies — password already confirmed correct, so it's
      // safe to tell the caller which societies this login belongs to, so it
      // can show the "pick your society" popup. Login is NOT finished yet —
      // the caller must call completeLoginWithSociety() once picked.
      const societyOptions = await Promise.all(
        societyIds.map(async (sid) => ({
          societyId: sid,
          name: await getSocietyDisplayName(sid),
        }))
      );

      return {
        success: true,
        needsSocietySelection: true,
        uid,
        baseUserData: data,
        societies: societyOptions,
      };
    } catch (err) {
      console.error("login: unexpected failure after sign-in:", err);
      return { success: false, error: "Something went wrong. Please check your connection and try again." };
    }
  }

  // ── LOGIN — Step 2 (only needed when login() returned ──────────
  // ── needsSocietySelection: true) ────────────────────────────────
  // Called by LoginScreen after the person picks a society from the popup.
  async function completeLoginWithSociety(uid, baseUserData, societyId) {
    try {
      const result = await fetchMemberAndBuildSession(baseUserData, uid, societyId);
      if (!result.success) return result;

      setUser(result.user);
      localStorage.setItem("abc_user", JSON.stringify(result.user));
      return { success: true, user: result.user };
    } catch (err) {
      console.error("completeLoginWithSociety failed:", err);
      return { success: false, error: "Something went wrong. Please check your connection and try again." };
    }
  }

  // ── LOGOUT ─────────────────────────────────────────────────
  // CHANGED: now also ends the real Firebase Auth session, not just
  // the app's own remembered state. Without this, the browser would
  // stay signed in to Firebase as that person even after "logging out".
  async function logout() {
    setUser(null);
    localStorage.removeItem("abc_user");
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Firebase sign-out failed:", err);
    }
  }

  // ── FORCED PASSWORD CHANGE — for accounts created via the setup
  // wizard / bulk upload with a default/temporary password the owner
  // never chose themself. Called from a "set your own password" screen
  // shown right after login when user.mustChangePassword is true.
  //
  // CHANGED: the new password is now set through Firebase Auth itself
  // (updatePassword), not saved into Firestore. Firestore no longer
  // stores passwords at all — Firebase is the only source of truth now.
  async function completeForcedPasswordChange(newPassword) {
    if (!user) return { success: false, error: "Not logged in." };
    try {
      if (!auth.currentUser) {
        return { success: false, error: "Your session has expired. Please log in again." };
      }
      await updatePassword(auth.currentUser, newPassword);
      await updateDoc(doc(db, "users", user.uid), {
        mustChangePassword: false,
      });
      const updated = { ...user, mustChangePassword: false };
      setUser(updated);
      localStorage.setItem("abc_user", JSON.stringify(updated));
      return { success: true };
    } catch (err) {
      console.error("completeForcedPasswordChange failed:", err);
      // Firebase requires a "recent login" for sensitive actions like this;
      // if it's been a while since sign-in, it may ask to log in again.
      if (err?.code === "auth/requires-recent-login") {
        return { success: false, error: "Please log out and log back in, then try again." };
      }
      return { success: false, error: "Couldn't update password. Try again." };
    }
  }

  // ── DPDP 2: CONSENT — saves the person's "I agree" to the privacy
  // notice. The write happens on the server (recordConsent function),
  // which checks who is signed in and records the version + date/time.
  // The browser cannot write these fields itself (blocked in
  // firestore.rules).
  async function acceptConsent() {
    if (!user) return { success: false, error: "Not logged in." };
    try {
      if (!auth.currentUser) {
        return { success: false, error: "Your session has expired. Please log in again." };
      }
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(RECORD_CONSENT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
          ...(await getAppCheckHeader()),
        },
        body: JSON.stringify({ version: CONSENT_VERSION }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        return { success: false, error: "Couldn't save your agreement. Please try again." };
      }
      const updated = { ...user, consentVersion: CONSENT_VERSION, consentAt: data.consentAt || null };
      setUser(updated);
      localStorage.setItem("abc_user", JSON.stringify(updated));
      return { success: true };
    } catch (err) {
      console.error("acceptConsent failed:", err);
      return { success: false, error: "Couldn't save your agreement. Please try again." };
    }
  }

  // ── PASSWORD RESET — Step 1: ask the Forgot Password Apps Script ──
  // ── to send the reset email. Called from LoginScreen "Forgot
  // ── Password" flow.
  // CHANGED (2026-08-12): the "users" collection is no longer publicly
  // readable, so this can no longer query Firestore directly by mobile.
  // The Forgot Password Apps Script (own service account) now does that
  // lookup server-side and triggers Firebase's own reset email itself —
  // this function never sees or handles the real email address, and the
  // script always returns the same generic response either way (by
  // design, so this endpoint can't be used to check which mobile
  // numbers are registered).
  async function generateResetToken(mobile) {
    const trimMobile = mobile.trim();
    try {
      const resp = await fetch(FORGOT_PASSWORD_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({ mobile: trimMobile }),
      });
      const result = await resp.json();
      return {
        success: true,
        message: result.message || "If that mobile number is registered, a reset link has been sent to the email on file.",
      };
    } catch (err) {
      console.error("generateResetToken: request failed:", err);
      return { success: false, error: "Something went wrong. Please check your connection and try again." };
    }
  }

  // ── WATCHMAN PIN RESET ──────────────────────────────────────
  // Moved out of AuthContext. Setting another user's Firebase Auth
  // password can't be done client-side, so this is now handled by the
  // Apps Script Web App backend (called directly from
  // SettingsScreen.jsx's handlePinReset) instead of living here.

  // ── PASSWORD RESET — Step 2: confirm the new password with ────────
  // ── Firebase Auth itself, using the oobCode from the reset link ───
  // CHANGED: oobCode replaces our old custom "token". Firebase
  // validates the code's authenticity and expiry on its own servers —
  // no Firestore lookup needed here at all.
  async function resetPassword(oobCode, newPassword) {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      return { success: true };
    } catch (err) {
      console.error("confirmPasswordReset failed:", err);
      if (err?.code === "auth/expired-action-code") {
        return { success: false, error: "Reset link has expired. Please request a new one." };
      }
      if (err?.code === "auth/invalid-action-code") {
        return { success: false, error: "Invalid or already-used reset link. Please request a new one." };
      }
      if (err?.code === "auth/weak-password") {
        return { success: false, error: "Password is too weak. Please choose at least 6 characters." };
      }
      return { success: false, error: "Could not reset password. Please try again." };
    }
  }

  // ── Role helpers ───────────────────────────────────────────
  const isPresident    = () => !!user?.roles?.includes("President");
  const isSecretary    = () => !!user?.roles?.includes("Secretary");
  const isTreasurer    = () => !!user?.roles?.includes("Treasurer");
  const isWatchman     = () => !!user?.roles?.includes("Watchman");
  const isMC           = () =>
    ["President", "Secretary", "Treasurer"].some((r) => user?.roles?.includes(r));
  const isFlatOwner    = () => !!user?.roles?.includes("FlatOwner");
  const isTenant       = () => user?.memberType === "tenant";
  const isFamilyMember = () => user?.memberType === "family";

  // Single canonical "which role should I show/save for this person" helper.
  // Replaces the old user.role (= roles[0], first-added-wins) shortcut, which
  // always showed "FlatOwner" first regardless of any MC role someone also
  // held. Precedence: President > Secretary > Treasurer > Watchman > FlatOwner
  // > whatever else is in roles[] > blank.
  function primaryRole() {
    if (isPresident()) return "President";
    if (isSecretary()) return "Secretary";
    if (isTreasurer()) return "Treasurer";
    if (isWatchman()) return "Watchman";
    if (isFlatOwner()) return "FlatOwner";
    return user?.roles?.[0] || "";
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        dpdpEnabled,
        login,
        completeLoginWithSociety,
        logout,
        completeForcedPasswordChange,
        acceptConsent,
        generateResetToken,
        resetPassword,
        isPresident,
        isSecretary,
        isTreasurer,
        isWatchman,
        isMC,
        isFlatOwner,
        isTenant,
        isFamilyMember,
        primaryRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
